import "@/assets/styles/custom_table.css";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import { useState } from "react";
import Delete from "@/components/common/alerts/Delete";
import { useSelector } from "react-redux";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";
import { Progress, Table, Dropdown, Avatar } from "@/config/antd";
// Common avatar (aliased) for real person photos; antd Avatar stays for initials
import UserAvatar from "@/components/common/Avatar";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

const CanvassTable = ({
  data = [],
  setSelectedHouseholds,
  onDelete,
}) => {
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedCanvass, setSelectedCanvass] = useState(null);
  const navigate = useNavigate();
  const globalSearchTerm = useSelector(selectSearchTerm) || "";

  // Apply global search filter to data
  const filteredData = globalSearchTerm
    ? data.filter(
        (item) =>
          item.householdName
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.address
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.canvasser
            ?.toLowerCase()
            .includes(globalSearchTerm.toLowerCase()) ||
          item.status?.toLowerCase().includes(globalSearchTerm.toLowerCase())
      )
    : data;

  const handleDeleteConfirm = async () => {
    if (selectedCanvass) {
      onDelete(selectedCanvass._id, selectedCanvass.householdName);
      setDeleteModalVisible(false);
      setSelectedCanvass(null);
    }
  };

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedHouseholds(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };

  return (
    <>
      <Table
        dataSource={filteredData}
        rowSelection={{
          type: "checkbox",
          ...rowSelection,
        }}
        pagination={false}
        onRow={(record) => ({
          onClick: () => navigate(`/dashboard/canvass/${record._id}`),
          style: { cursor: "pointer" },
        })}
        rowKey={(record) => record._id}>
        <>
          <Column
            title="Household Name"
            dataIndex="householdName"
            key="householdName"
            render={(householdName, record) => (
              <Link
                to={`/dashboard/canvass/${record._id}`}
                className="flex items-center gap-3"
                onClick={(e) => e.stopPropagation()}>
                <Avatar
                  size={40}
                  shape="circle"
                  style={{
                    backgroundColor: "#0F75BC",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                  }}>
                  {householdName?.charAt(0) || "N"}
                </Avatar>
                <div className="flex flex-col">
                  <span className="font-bold text-bold">
                    {householdName || "No Name"}
                  </span>
                  <span className="text-xs text-lighterGrey text-regular truncate w-[200px]">
                    {record.address || "No Address Provided"}
                  </span>
                </div>
              </Link>
            )}
          />
          <Column
            title="Members"
            dataIndex="members"
            key="members"
            render={(members) => (
              <span className="text-regular">{members || 0} People</span>
            )}
          />
          <Column
            title="Canvasser"
            dataIndex="canvasser"
            key="canvasser"
            width={180}
            render={(canvasser) => (
              <div className="flex items-center gap-2">
                <UserAvatar size={32} alt={canvasser || "Unassigned"} />
                <span className="text-regular font-bold truncate w-[140px]">
                  {canvasser || "Unassigned"}
                </span>
              </div>
            )}
          />
          <Column
            title="Support Level"
            key="supportLevel"
            dataIndex="supportLevel"
            width={100}
            render={(supportLevel) => (
              <div className="w-full" style={{ minWidth: "100px" }}>
                <Progress
                  percent={supportLevel || 0}
                  showInfo={false}
                  strokeColor="#0F75BC"
                  trailColor="#E8F4FC"
                  size={[null, 10]}
                  className="w-full"
                  style={{ width: "100%" }}
                />
              </div>
            )}
          />
          <Column
            title="Date and Time"
            key="dateTime"
            dataIndex="dateTime"
            render={(dateTime) => (
              <div className="text-small">{dateTime || "No Date"}</div>
            )}
          />
          <Column
            title="Status"
            key="status"
            dataIndex="status"
            render={(status) => (
              <div
                className={`text-regular font-bold ${
                  status === "Complete"
                    ? "text-green"
                    : status === "Pending"
                    ? "text-warning"
                    : status === "Rejected"
                    ? "text-danger"
                    : "text-secondary"
                }`}>
                {status || "Unknown"}
              </div>
            )}
          />
          <Column
            title=""
            key="action"
            render={() => (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "modify",
                      label: "Modify",
                      // onClick: (e) => {
                      //     e.domEvent.stopPropagation();
                      //     handleMenuClick({ key: 'modify' }, record);
                      // }
                    },
                    {
                      key: "delete",
                      label: "Delete",
                      // onClick: (e) => {
                      //     e.domEvent.stopPropagation();
                      //     handleMenuClick({ key: 'delete' }, record);
                      // }
                    },
                  ],
                }}
                trigger={["click"]}>
                <CustomMoreOptions
                  variant="filled"
                  onClick={(e) => e.stopPropagation()}
                  color="black"
                  orientation="vertical"
                  size="small"
                />
              </Dropdown>
            )}
          />
        </>
      </Table>

      <Delete
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Canvass Record?"
        paragraph={
          selectedCanvass
            ? `Are you sure you want to delete record for "${
                selectedCanvass.householdName || "this household"
              }"?`
            : ""
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

CanvassTable.propTypes = {
  data: PropTypes.array,
  setSelectedHouseholds: PropTypes.func,
  onDelete: PropTypes.func,
};

export default CanvassTable;
